import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthorityContext = createContext({
  authorities: [],
  defaultAuthorities: [],
  customAuthorities: [],
  loading: false,
  fetchAuthorities: () => Promise.resolve(),
  addAuthority: () => Promise.resolve(),
  deleteAuthority: () => Promise.resolve(),
});

export const useAuthority = () => useContext(AuthorityContext);

export const AuthorityProvider = ({ children }) => {
  const [defaultAuthorities, setDefaultAuthorities] = useState([]);
  const [customAuthorities, setCustomAuthorities] = useState([]);
  const [loading, setLoading] = useState(false);

  const getToken = () => {
    try {
      const raw = localStorage.getItem('token');
      return raw ? raw.replace(/^"|"$/g, '') : null;
    } catch { return null; }
  };

  const fetchAuthorities = useCallback(async () => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BASE_URL}authority/get`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDefaultAuthorities(res.data?.result?.default_authorities || []);
      setCustomAuthorities(res.data?.result?.custom_authorities || []);
    } catch (err) {
      console.warn('Failed to fetch authorities:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const addAuthority = useCallback(async (name) => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}authority/add`,
        { name },
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      setDefaultAuthorities(res.data?.result?.default_authorities || []);
      setCustomAuthorities(res.data?.result?.custom_authorities || []);
    } catch (err) {
      console.error('Failed to add authority:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteAuthority = useCallback(async (name) => {
    const token = getToken();
    if (!token) return;
    try {
      setLoading(true);
      const res = await axios.delete(
        `${process.env.REACT_APP_BASE_URL}authority/delete`,
        {
          data: { name },
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      setDefaultAuthorities(res.data?.result?.default_authorities || []);
      setCustomAuthorities(res.data?.result?.custom_authorities || []);
    } catch (err) {
      console.error('Failed to delete authority:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // All authority names combined (for dropdowns)
  const authorities = [...defaultAuthorities.map(a => a.name), ...customAuthorities.map(a => a.name)];

  useEffect(() => {
    const token = getToken();
    if (token) fetchAuthorities();
  }, [fetchAuthorities]);

  return (
    <AuthorityContext.Provider value={{
      authorities,
      defaultAuthorities,
      customAuthorities,
      loading,
      fetchAuthorities,
      addAuthority,
      deleteAuthority,
    }}>
      {children}
    </AuthorityContext.Provider>
  );
};

export default AuthorityContext;
